import { Autocomplete, Box, CircularProgress, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import debounce from 'lodash/debounce';
import { memo, useEffect, useMemo, useState } from 'react';
import { botelService, Location } from '../services/api/botelService';

interface LocationSearchProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const LocationSearch = memo(({ value, onChange, error }: LocationSearchProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalLocations, setTotalLocations] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchLocations = async (query?: string, pageNum: number = 1) => {
    try {
      setLoading(true);
      const response = await botelService.searchLocations(query, pageNum);
      if (pageNum === 1) {
        setLocations(response.locations);
      } else {
        setLocations(prev => [...prev, ...response.locations]);
      }
      setTotalLocations(response.total);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial locations
  useEffect(() => {
    fetchLocations();
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(async (query: string) => {
      if (query.length >= 2) {
        setPage(1);
        await fetchLocations(query, 1);
      } else if (query.length === 0) {
        await fetchLocations();
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLDivElement;
    if (
      !loading &&
      target.scrollTop + target.clientHeight >= target.scrollHeight - 20 &&
      locations.length < totalLocations
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLocations(searchQuery, nextPage);
    }
  };

  return (
    <>
      <Typography 
        variant="subtitle2" 
        mb={1} 
        color="#fff"
        sx={{ fontSize: { xs: 14, sm: 16 } }}
      >
        Location
      </Typography>
      <Autocomplete
        freeSolo
        options={locations}
        getOptionLabel={(option) => 
          typeof option === 'string' ? option : option.location
        }
        inputValue={searchQuery}
        onInputChange={(_, newValue) => {
          setSearchQuery(newValue);
        }}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') {
            onChange(newValue);
          } else if (newValue) {
            onChange(newValue.location);
          }
        }}
        ListboxProps={{
          onScroll: handleScroll,
          style: { maxHeight: '200px' }
        }}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            error={!!error}
            sx={{ 
              bgcolor: '#1a1f24', 
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                fontSize: { xs: 14, sm: 16 },
                '& fieldset': {
                  borderColor: error ? 'error.main' : 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& input': {
                  fontSize: { xs: 14, sm: 16 },
                  padding: { xs: '10px 14px', sm: '12px 14px' }
                }
              },
            }}
            placeholder="Search for a location"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={isMobile ? 18 : 20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography 
                variant="body1"
                sx={{ fontSize: { xs: 14, sm: 16 } }}
              >
                {option.location}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: { xs: 12, sm: 14 } }}
              >
                {option.location_address}
              </Typography>
            </Box>
          </li>
        )}
      />
      {error && (
        <Typography 
          color="error" 
          variant="caption"
          sx={{ fontSize: { xs: 12, sm: 14 } }}
        >
          {error}
        </Typography>
      )}
    </>
  );
});

export default LocationSearch; 